import { isEmpty } from 'es-toolkit/compat';
import { ComponentType, ReactNode } from 'react';

import { Ranking } from '@flipster/apis/v2/trading-competition/types';
import { Styles, css } from '@flipster/styled-system/css';
import { center, grid, hstack, stack, vstack } from '@flipster/styled-system/patterns';

const TableHeaderItem = ({ align, children }: { align: 'left' | 'right' | 'center'; children: ReactNode }) => {
  const justify = align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';
  return <div className={hstack({ justify })}>{children}</div>;
};

export interface TableHeaderItemProps {
  title: ReactNode;
  icon?: ReactNode;
  align?: 'left' | 'right' | 'center';
}

const TableHeader = ({ items }: { items: Array<TableHeaderItemProps> }) => {
  return (
    <div
      className={grid({
        columns: items.length,
        h: 36,
        pb: 12,
        px: { base: 16, tablet: 24 },
        color: 'content.default.level-1',
        textStyle: 'Text/M/Regular',
      })}
    >
      {items.map((item, index) => {
        return (
          <TableHeaderItem key={index} align={item.align ?? 'left'}>
            <span>{item.title}</span>
            {item.icon}
          </TableHeaderItem>
        );
      })}
    </div>
  );
};

export interface TableProps {
  noItem?: ReactNode;
  headerItems: Array<TableHeaderItemProps>;
  items?: Array<Ranking>;
  Row: ComponentType<{ item: Ranking; index: number }>;
  id?: string;
  css?: Styles;
}

export const Table = ({ noItem, headerItems, items, Row, css: cssProps, id }: TableProps) => {
  if (isEmpty(items)) {
    if (!noItem) return null;
    return (
      <div
        className={css(
          center.raw({
            flexDir: 'column',
            gap: 16,
            p: 40,
            borderColor: 'border.default.level-2',
            borderWidth: '1px',
            borderStyle: 'solid',
            rounded: 16,
            h: { base: 460, tablet: 716 },
          }),
          cssProps,
        )}
      >
        {noItem}
      </div>
    );
  }

  return (
    <div className={css(stack.raw({ gap: 8 }), cssProps)} id={id}>
      <TableHeader items={headerItems} />
      <ul className={vstack({ gap: 8 })}>
        {items?.map((item, index) => {
          return <Row key={`${item.rank}-${item.nickname}`} item={item} index={index} />;
        })}
      </ul>
    </div>
  );
};

export const DesktopTable = ({ noItem, headerItems, items, Row, id }: TableProps) => {
  return (
    <Table
      id={id}
      noItem={noItem}
      headerItems={headerItems}
      items={items}
      Row={Row}
      css={{ tabletDown: { display: 'none' } }}
    />
  );
};

export const MobileTable = ({ noItem, headerItems, items, Row, id }: TableProps) => {
  return (
    <Table
      id={id}
      noItem={noItem}
      headerItems={headerItems}
      items={items}
      Row={Row}
      css={{ tablet: { display: 'none' } }}
    />
  );
};
